import React from 'react';
import { useSelector } from 'react-redux';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { Store } from 'components/common/types/Store.types';
import { DirectSalesLineObjective } from 'components/common/types/DirectSalesCampaign.types';
import Location from '../FiltersContent/Location';
import { ORDERED_DSM_LOCATION_CATEGORY } from '../FiltersContent/Location/utils';
import Formats from '../DirectSalesFilterPanels/Formats';
import Tags from '../DirectSalesFilterPanels/Tags';
import FrameListPanel from './FrameListPanel';
import POIPanel from '../DataTargetingContent/PointsOfInterest/POIPanel';
import useObjectivesAllowedSelection from './useObjectivesAllowedSelection';

type ObjectiveSubPanelsProps = {
  isSecondaryPanelDisplayed: boolean;
  handleClosePanel: VoidFunction;
  onFilterChange: (data: Partial<DirectSalesLineObjective>) => void;
};

const ObjectiveSubPanels: React.FC<ObjectiveSubPanelsProps> = ({
  isSecondaryPanelDisplayed,
  handleClosePanel,
  onFilterChange,
}: ObjectiveSubPanelsProps) => {
  const {
    data: activeObjective,
    data: {
      locations,
      formats,
      tags,
      poi,
      frameList: { routeFrameCodes, listFiles },
    },
  } = useSelector((state: Store) => state.objective);
  const { allowedLocationOptions, allowedFormatOptions, allowedTagOptions } =
    useObjectivesAllowedSelection(activeObjective);
  const plannerSecondaryPanelType = useSelector((state: Store) => state.dealManagement.plannerSecondaryPanelType);

  if (!isSecondaryPanelDisplayed) return null;

  return (
    <>
      {plannerSecondaryPanelType === PlannerSecondaryPanelType.OBJECTIVE_LOCATIONS && (
        <Location
          locationOptions={allowedLocationOptions}
          selectedLocations={locations}
          onConfirm={(newLocations) => onFilterChange({ locations: newLocations })}
          cancelButtonLabel="Back"
          isSubsection
          categoryOrder={ORDERED_DSM_LOCATION_CATEGORY}
        />
      )}
      {plannerSecondaryPanelType === PlannerSecondaryPanelType.OBJECTIVE_FORMAT && (
        <Formats
          formatOptions={allowedFormatOptions}
          selectedFormats={formats}
          onConfirm={(newFormats) => onFilterChange({ formats: newFormats })}
          cancelButtonLabel="Back"
          isSubsection
        />
      )}
      {plannerSecondaryPanelType === PlannerSecondaryPanelType.OBJECTIVE_POI && (
        <POIPanel
          selectedPOIs={poi}
          onConfirm={(newPoi) => onFilterChange({ poi: newPoi })}
          onClose={handleClosePanel}
          cancelButtonLabel="Back"
          isSubsection
        />
      )}
      {plannerSecondaryPanelType === PlannerSecondaryPanelType.OBJECTIVE_TAGS && (
        <Tags
          tagOptions={allowedTagOptions}
          selectedTags={tags}
          onConfirm={(newTags) => onFilterChange({ tags: newTags })}
          cancelButtonLabel="Back"
          isSubsection
        />
      )}
      {plannerSecondaryPanelType === PlannerSecondaryPanelType.OBJECTIVE_FRAME_LIST && (
        <FrameListPanel
          onConfirm={onFilterChange}
          onCancel={handleClosePanel}
          initialSelectedRouteFrameCodes={routeFrameCodes}
          initialUploadedListOfFiles={listFiles}
        />
      )}
    </>
  );
};

export default ObjectiveSubPanels;
